import { EMAIL_REQUEST_TIMEOUT, NEW_KEY_NAME, UPLOAD_IMAGE_REQUEST_TIMEOUT } from "~/const";
import { fetchJSON, fetchFile, getSearchParams, getApiPath, abortFetchSignal } from "~/utils/utils";
import { IAdminJournalItem, IAdminUpdateProjectBusinessPlan, IAdminUpdateProjectBillingMethod, IAdminUpdateProjectVIP, IProjectNotification, IProjectNotificationBody, IAdminSearchProject, IAdminSearchProjectPagination,
	IAdminSearchProjectFilter, IAdminBulkEmailData, IAdminTestEmailData, IAdminSetAdminMessagesData, IAdminComment, ICreateAdminCommentData, IAdminSalesAttributes, IAdminSalesAttributesChangeData, IUpdateAdminUserData,
	IAdminCatalogsFilter, IAdminCatalogsPagination, IQuickCommentItem, IAdminProjectMetaData, IChangeDebtConsumptionData, IAdminAssociatedEmails, IAdminQuickCommentData } from "./admin-interfaces";
import { IMeResponse, IClientProject, IClientProjectInvitation, IClientWalletInvitation, IClientWallet, IClientProjectWalletInvitation, ICreateProjectWalletInvitation, ISignNewConsent, IClientProjectCatalog, IClientProjectCatalogGallery } from "./client-interfaces";
import { ITransferOwnership, IInvite, IProjectOutput, IProjectsOutput, IKeyOutput, IKeysOutput, IProjectUsersOutput,
	IProjectConsumptionsOutput, IUpdateKey, IUpdateProject, IProjectUserOutput, IChangeProjectUser, ILastUpdateOutput, IService,
	ICreateService, IInternalUser, ICreateInternalUser, IUpdateInternalUser, IUser, ICatalogItem,
	ICreateProject, ICreateProjectWithKey, IApiCall, IProjectBillingHistory, IAdvancedBillingConsent, ISuggestProjectName, ITransactionApproval,
	IUpdateProjectBillingConsumption, IUpdateProjectBillingConsumptionOutput, IProjectAddUser, IChanceProjectNotificationData, IReportItem, IEditServiceData, IProjectAdminMessagesOutput, IProjectCatalogUpdateData,
	IProjectCatalogAdminSettingsData, IUserAdmin, IServiceLittle, IUpdateKeyServices } from "./interfaces";
import { IFetchOutput } from "~/interfaces";
import { TExportType } from "~/components/BillingExportSelect";

export function getCustomOutput<T>(data: T): IFetchOutput<T> {
	return {
		data,
		requestData: { data },
		noErrors: true,
		errors: [],
		status: 200,
		lastUpdate: "",
	};
}

/* eslint-disable-next-line */
export function sleep(timeout = 500) {
	return new Promise(resolve => setTimeout(() => resolve({}), timeout));
}

function getFilterParams(filterObj: any, lang: string) {
	const searchParams: any = {
		lang,
	};

	Object.keys(filterObj).forEach(filterKey => {
		const value = filterObj[filterKey];

		if (typeof value === "string" && !value) {
			return;
		}

		searchParams[filterKey] = value.toString();
	});

	return searchParams;
}

/* obecne volani */
export function apiMe(lang: string) {
	return fetchJSON<IMeResponse>(`${getApiPath("client")}/global/information/me${getSearchParams({ lang })}`);
}

export function apiSetGlobalLang(lang: string) {
	const url = `${getApiPath("client")}/global/language${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "PUT",
		body: JSON.stringify({}),
	});
}

/* ********** projekty ************** */
export function apiGetProject<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}${getSearchParams({ lang })}`;

	return fetchJSON<IProjectOutput[T]>(url);
}

export function apiGetProjects<T extends keyof IApiCall>(type: T, lang: string) {
	const url = `${getApiPath(type)}/project/${getSearchParams({ lang })}`;

	return fetchJSON<IProjectsOutput[T]>(url);
}

export function apiGetAdminProjects(filter: IAdminSearchProjectFilter, lang: string) {
	const searchParams = getFilterParams(filter, lang);

	// pri volani - pridame tohle
	if (filter.sortByBilledConsumption) {
		searchParams.sortByBillingMethod = filter.sortByBilledConsumption;
	}

	const url = `${getApiPath("admin")}/project/${getSearchParams(searchParams)}`;

	return fetchJSON<Array<IAdminSearchProject>, IAdminSearchProjectPagination>(url);
}

export function apiGetProjectUsers<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/user${getSearchParams({ lang })}`;

	return fetchJSON<IProjectUsersOutput[T]>(url);
}

export function apiGetProjectNotifications(projectId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/notification${getSearchParams({ lang })}`;

	return fetchJSON<Array<IProjectNotification>>(url);
}

export function apiGetProjectNotificationBody(projectId: number, notificationId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/notification/${notificationId}${getSearchParams({ lang })}`;

	return fetchJSON<IProjectNotificationBody>(url);
}

export function apiGetProjectConsumption<T extends keyof IApiCall>(type: T, projectId: number, fromDate: string, tillDate: string, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/consumption${getSearchParams({
		lang,
		...fromDate ? { fromDate } : {},
		...tillDate ? { tillDate } : {},
	})}`;

	return fetchJSON<IProjectConsumptionsOutput[T]>(url);
}

export function apiGetProjectCatalog<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog${getSearchParams({
		lang,
	})}`;

	return fetchJSON<IClientProjectCatalog>(url);
}

export function apiUpdateProjectCatalog<T extends keyof IApiCall>(type: T, projectId: number, catalogData: IProjectCatalogUpdateData, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog${getSearchParams({
		lang,
	})}`;

	return fetchJSON<IClientProjectCatalog>(url, {
		method: "PUT",
		body: JSON.stringify(catalogData),
	});
}

export function apiToggleProjectCatalogPublishStatus<T extends keyof IApiCall>(type: T, projectId: number, catalogId: string, publish: boolean, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog/${catalogId}${getSearchParams({
		lang,
		publish: publish ? "true" : "false",
	})}`;

	return fetchJSON<IClientProjectCatalog>(url, {
		method: "POST",
		body: JSON.stringify({}),
	});
}

export function apiDeleteProjectCatalog<T extends keyof IApiCall>(type: T, projectId: number, catalogId: string, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog/${catalogId}${getSearchParams({
		lang,
	})}`;

	return fetchJSON<IClientProjectCatalog>(url, {
		method: "DELETE",
		body: JSON.stringify({}),
	});
}

export function apiUpdateProjectCatalogAdminSettings(projectId: number, catalogId: string, updateData: IProjectCatalogAdminSettingsData, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/catalog/${catalogId}/settings${getSearchParams({
		lang,
	})}`;

	return fetchJSON<IClientProjectCatalog>(url, {
		method: "POST",
		body: JSON.stringify(updateData),
	});
}

export function apiGetProjectCatalogGallery<T extends keyof IApiCall>(type: T, projectId: number, catalogId: string, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog/${catalogId}/gallery${getSearchParams({
		lang,
	})}`;

	return fetchJSON<Array<IClientProjectCatalogGallery>>(url);
}

export function apiUploadProjectCatalogGalleryItem<T extends keyof IApiCall>(type: T, projectId: number, catalogId: string, image: File, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog/${catalogId}/gallery${getSearchParams({
		lang,
	})}`;
	const formData = new FormData();

	formData.append("image", image);

	return fetchJSON<Array<IClientProjectCatalogGallery>>(url, {
		method: "POST",
		body: formData,
		signal: abortFetchSignal(UPLOAD_IMAGE_REQUEST_TIMEOUT),
	});
}

export function apiSortProjectCatalogGallery<T extends keyof IApiCall>(type: T, projectId: number, catalogId: string, imageIds: Array<string>, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog/${catalogId}/gallery${getSearchParams({
		lang,
	})}`;

	return fetchJSON<Array<IClientProjectCatalogGallery>>(url, {
		method: "PUT",
		body: JSON.stringify({
			imageIds,
		}),
	});
}

export function apiDeleteProjectCatalogGalleryItem<T extends keyof IApiCall>(type: T, projectId: number, catalogId: string, imageId: string, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/catalog/${catalogId}/gallery/${imageId}${getSearchParams({
		lang,
	})}`;

	return fetchJSON<Array<IClientProjectCatalogGallery>>(url, {
		method: "DELETE",
		body: JSON.stringify({}),
	});
}

export function apiCreateProject<T extends keyof IApiCall>(type: T, createData: ICreateProject[T], lang: string) {
	const url = `${getApiPath(type)}/project/${getSearchParams({ lang })}`;

	return fetchJSON<IProjectOutput[T]>(url, {
		method: "POST",
		body: JSON.stringify(createData),
	});
}

export function apiUpdateProject<T extends keyof IApiCall>(type: T, projectId: number, updateData: IUpdateProject[T], lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}${getSearchParams({ lang })}`;
	const copiedUpdateData = {
		...updateData,
	};

	if ("validTill" in copiedUpdateData && copiedUpdateData.validTill === "") {
		delete copiedUpdateData.validTill;
	}

	return fetchJSON<IProjectOutput[T]>(url, {
		method: "PUT",
		body: JSON.stringify(copiedUpdateData),
	});
}

export function apiUpdateProjectBillingConsumption<T extends keyof IApiCall>(type: T, projectId: number, updateData: IUpdateProjectBillingConsumption[T], lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/billed-consumption${getSearchParams({ lang })}`;

	return fetchJSON<IUpdateProjectBillingConsumptionOutput[T]>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiUpdateProjectBusinessPlan(projectId: number, updateData: IAdminUpdateProjectBusinessPlan, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/business-plan${getSearchParams({ lang })}`;

	return fetchJSON<IAdminUpdateProjectBusinessPlan>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiUpdateProjectBillingMethod(projectId: number, updateData: IAdminUpdateProjectBillingMethod, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/billing-method${getSearchParams({ lang })}`;

	return fetchJSON<IAdminUpdateProjectBillingMethod>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiUpdateProjectVIP(projectId: number, updateData: IAdminUpdateProjectVIP, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/vip${getSearchParams({ lang })}`;

	return fetchJSON<IAdminUpdateProjectVIP>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiTransferProject<T extends keyof IApiCall>(type: T, projectId: number, transferData: ITransferOwnership, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/transfer${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "POST",
		body: JSON.stringify(transferData),
	});
}

export function apiRemoveUser<T extends keyof IApiCall>(type: T, projectId: number, userId: number, lastUpdate: string, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/user/${userId}${getSearchParams({ lang })}`;

	return fetchJSON<ILastUpdateOutput>(url, {
		method: "DELETE",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

export function apiRemoveProjectInvitation<T extends keyof IApiCall>(type: T, projectId: number, invitationId: string, lastUpdate: string, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/user/invitation/${invitationId}${getSearchParams({ lang })}`;

	return fetchJSON<ILastUpdateOutput>(url, {
		method: "DELETE",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

export function apiUpdateProjectUser<T extends keyof IApiCall>(type: T, projectId: number, userId: number, changeUserProjectData: IChangeProjectUser[T], lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/user/${userId}${getSearchParams({ lang })}`;

	return fetchJSON<IProjectUserOutput[T]>(url, {
		method: "PUT",
		body: JSON.stringify(changeUserProjectData),
	});
}

export function apiUpdateProjectUserNotification<T extends keyof IApiCall>(type: T, projectId: number, userId: number, updateData: IChanceProjectNotificationData, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/user-notifications/${userId}${getSearchParams({ lang })}`;

	return fetchJSON<IProjectUserOutput[T]>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiGetProjectInvitations<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/user/invitation${getSearchParams({ lang })}`;

	return fetchJSON<Array<IClientProjectInvitation>>(url);
}

export function apiInviteUserProject(projectId: number, inviteData: IInvite, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/user/invite${getSearchParams({ lang })}`;

	return fetchJSON<IClientProject>(url, {
		method: "POST",
		body: JSON.stringify(inviteData),
	});
}

export function apiAddUserProject(projectId: number, addData: IProjectAddUser, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/user${getSearchParams({ lang })}`;

	return fetchJSON<IUser>(url, {
		method: "POST",
		body: JSON.stringify(addData),
	});
}

export function apiLeaveProject(projectId: number, lastUpdate: string, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/leave${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "DELETE",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

export function apiGetProjectBillingHistory<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/billing-history${getSearchParams({ lang })}`;

	return fetchJSON<Array<IProjectBillingHistory>>(url, {
		method: "GET",
	});
}

export function apiProjectBillingHistoryCheck<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/billing-history/check${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "GET",
	});
}

export function apiToggleProjectBillingDebt<T extends keyof IApiCall>(type: T, projectId: number, billingId: number, debt: boolean, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/billing-history/${billingId}${getSearchParams({
		lang,
		debt: debt.toString(),
	})}`;

	return fetchJSON<Array<IProjectBillingHistory>>(url, {
		method: "POST",
	});
}

export function apiGetProjectAdvancedBillingConsent<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/advanced-billing-consent${getSearchParams({ lang })}`;

	return fetchJSON<Array<IAdvancedBillingConsent>>(url, {
		method: "GET",
	});
}

export function apiSignNewConsent(projectId: number, consentData: ISignNewConsent, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/advanced-billing-consent${getSearchParams({ lang })}`;

	return fetchJSON<IAdvancedBillingConsent>(url, {
		method: "POST",
		body: JSON.stringify(consentData),
	});
}

export function apiCancelConsent(projectId: number, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/advanced-billing-consent${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "DELETE",
	});
}

export function apiGetProjectSuggestName(projectTitle: string, lang: string) {
	const url = `${getApiPath("client")}/project/suggest/name${getSearchParams({ lang })}`;

	return fetchJSON<ISuggestProjectName>(url, {
		method: "POST",
		body: JSON.stringify({
			projectTitle,
		}),
	});
}

export function apiToggleProjectArchivation<T extends keyof IApiCall>(type: T, projectId: number, archive: boolean, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/archive${getSearchParams({ lang })}`;

	return fetchJSON<ILastUpdateOutput>(url, {
		method: "PUT",
		body: JSON.stringify({
			archive,
		}),
	});
}

export function apiGetProjectAssociatedEmails(projectId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/associated-email-addresses${getSearchParams({ lang })}`;

	return fetchJSON<Array<IAdminAssociatedEmails>>(url);
}

/* ********** zpravy ************** */
export function apiGetProjectAdminMessages<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/admin-messages${getSearchParams({ lang })}`;

	return fetchJSON<IProjectAdminMessagesOutput[T]>(url, {
		method: "GET",
	});
}

export function apiSetProjectAdminMessages(projectId: number, putData: IAdminSetAdminMessagesData, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/admin-messages${getSearchParams({ lang })}`;

	return fetchJSON<IProjectAdminMessagesOutput["admin"]>(url, {
		method: "PUT",
		body: JSON.stringify(putData),
	});
}

export function apiRemoveProjectAdminMessages(projectId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/admin-messages${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "DELETE",
	});
}

/* ********** komentare ************** */
export function apiGetProjectAdminComments(projectId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/sales-comments${getSearchParams({ lang })}`;

	return fetchJSON<Array<IAdminComment>>(url, {
		method: "GET",
	});
}

export function apiCreateProjectAdminComment(projectId: number, commentData: ICreateAdminCommentData, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/sales-comments${getSearchParams({ lang })}`;

	return fetchJSON<IAdminComment>(url, {
		method: "POST",
		body: JSON.stringify(commentData),
	});
}

export function apiEditProjectAdminComment(projectId: number, commentId: number, commentData: ICreateAdminCommentData, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/sales-comments/${commentId}${getSearchParams({ lang })}`;

	return fetchJSON<IAdminComment>(url, {
		method: "PUT",
		body: JSON.stringify(commentData),
	});
}

export function apiRemoveProjectAdminComment(projectId: number, commentId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/sales-comments/${commentId}${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "DELETE",
	});
}

/* ********** obchodni polozky ************** */
export function apiGetProjectAdminSalesAttributes(projectId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/sales-attributes${getSearchParams({ lang })}`;

	return fetchJSON<IAdminSalesAttributes>(url, {
		method: "GET",
	});
}

export function apiUpdateProjectAdminSalesAttributes(projectId: number, updateData: IAdminSalesAttributesChangeData, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/sales-attributes${getSearchParams({ lang })}`;

	return fetchJSON<IAdminSalesAttributes>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

/* ********** journal ************** */
export function apiGetProjectJournal(projectId: number, lang: string) {
	const url = `${getApiPath("admin")}/journal/${getSearchParams({
		lang,
		// pro ziskani dat
		detail: "true",
		projectId: projectId.toString(),
	})}`;

	return fetchJSON<Array<IAdminJournalItem>>(url, {
		method: "GET",
	});
}

/* ********** pozvanka do projektu ************** */
export function apiGeProjectInvitationById(invitationId: string, lang: string) {
	const url = `${getApiPath("client")}/invitation/project/${invitationId}${getSearchParams({ lang })}`;

	return fetchJSON<IClientProjectInvitation>(url);
}

export function apiConfirmProjectInvitation(invitationId: string, lang: string) {
	const url = `${getApiPath("client")}/invitation/project/${invitationId}/confirm${getSearchParams({ lang })}`;

	return fetchJSON<IClientProject>(url, {
		method: "POST",
	});
}

export function apiRejectProjectInvitation(invitationId: string, lang: string) {
	const url = `${getApiPath("client")}/invitation/project/${invitationId}/refuse${getSearchParams({ lang })}`;

	return fetchJSON<IClientProject>(url, {
		method: "POST",
	});
}

/* ********** pozvanka do penezenky ************** */
export function apiGetWalletInvitationById(invitationId: string, lang: string) {
	const url = `${getApiPath("client")}/invitation/wallet/${invitationId}${getSearchParams({ lang })}`;

	return fetchJSON<IClientWalletInvitation>(url);
}

export function apiConfirmWalletInvitation(invitationId: string, lang: string) {
	const url = `${getApiPath("client")}/invitation/wallet/${invitationId}/confirm${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "POST",
	});
}

export function apiRejectWalletInvitation(invitationId: string, lang: string) {
	const url = `${getApiPath("client")}/invitation/wallet/${invitationId}/refuse${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "POST",
	});
}

/* ********** penezenka ************** */
export function apiGetProjectWallet<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/wallet${getSearchParams({ lang })}`;

	// stejne pro admina
	return fetchJSON<IClientWallet>(url, {
		method: "GET",
	});
}

export function apiCreateProjectWallet(projectId: number, lastUpdate: string, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/wallet${getSearchParams({ lang })}`;

	return fetchJSON<IClientWallet>(url, {
		method: "POST",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

export function apiRemoveProjectWallet(projectId: number, lastUpdate: string, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/wallet${getSearchParams({ lang })}`;

	return fetchJSON<any>(url, {
		method: "DELETE",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

export function apiGetProjectWalletInvitation<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/wallet/invitation${getSearchParams({ lang })}`;

	// stejne pro admina
	return fetchJSON<IClientProjectWalletInvitation>(url, {
		method: "GET",
	});
}

export function apiCreateProjectWalletInvitation(projectId: number, createData: ICreateProjectWalletInvitation, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/wallet/invitation${getSearchParams({ lang })}`;

	return fetchJSON<IClientProjectWalletInvitation>(url, {
		method: "PUT",
		body: JSON.stringify(createData),
	});
}

export function apiRevokeProjectWalletInvitation(projectId: number, lastUpdate: string, lang: string) {
	const url = `${getApiPath("client")}/project/${projectId}/wallet/invitation${getSearchParams({ lang })}`;

	return fetchJSON<any>(url, {
		method: "DELETE",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

/* ********** klice ************** */
export function apiGetProjectKey<T extends keyof IApiCall>(type: T, projectId: number, keyId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey/${keyId}${getSearchParams({ lang })}`;

	return fetchJSON<IKeyOutput[T]>(url);
}

export function apiGetProjectKeys<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey${getSearchParams({ lang })}`;

	return fetchJSON<IKeysOutput[T]>(url);
}

export function apiUpdateKey<T extends keyof IApiCall>(type: T, projectId: number, keyId: number, updateData: IUpdateKey[T], lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey/${keyId}${getSearchParams({ lang })}`;

	return fetchJSON<IKeyOutput[T]>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	}, {
		pretty422: true,
	});
}

export function apiUpdateKeyServices<T extends keyof IApiCall>(type: T, projectId: number, keyId: number, updateData: IUpdateKeyServices, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey/${keyId}/services${getSearchParams({ lang })}`;

	return fetchJSON<Array<IServiceLittle>>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiCreateKey<T extends keyof IApiCall>(type: T, projectId: number, createData: IUpdateKey[T], lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey${getSearchParams({ lang })}`;

	return fetchJSON<IKeyOutput[T]>(url, {
		method: "POST",
		body: JSON.stringify(createData),
	});
}

export function apiGetApiKeyServices<T extends keyof IApiCall>(type: T, projectId: number, apiKey: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey/${apiKey}/services${getSearchParams({ lang })}`;

	return fetchJSON<Array<IServiceLittle>>(url);
}

export function apiGetKeyConsumption<T extends keyof IApiCall>(type: T, projectId: number, keyId: number, fromDate: string, tillDate: string, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey/${keyId}/consumption${getSearchParams({
		lang,
		...fromDate ? { fromDate } : {},
		...tillDate ? { tillDate } : {},
	})}`;

	return fetchJSON<IProjectConsumptionsOutput[T]>(url);
}

export function apiRemoveKey<T extends keyof IApiCall>(type: T, projectId: number, keyId: number, lastUpdate: string, lang: string) {
	return fetchJSON<null>(`${getApiPath(type)}/project/${projectId}/apikey/${keyId}${getSearchParams({ lang })}`, {
		method: "DELETE",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

/* ********** admin - service ************** */
export function apiGetServices(lang: string) {
	const url = `${getApiPath("admin")}/service/${getSearchParams({ lang })}`;

	return fetchJSON<Array<IService>>(url);
}

export function apiGetQueryTypesServices() {
	const url = `${getApiPath("admin")}/service/${getSearchParams({ queryTypesOnly: "true" })}`;

	return fetchJSON<Array<string>>(url);
}

export function apiCreateService(createData: ICreateService, lang: string) {
	const url = `${getApiPath("admin")}/service/${getSearchParams({ lang })}`;

	return fetchJSON<IService>(url, {
		method: "POST",
		body: JSON.stringify(createData),
	});
}

export function apiEditService(serviceId: number, createData: IEditServiceData, lang: string) {
	const url = `${getApiPath("admin")}/service/${serviceId}${getSearchParams({ lang })}`;

	return fetchJSON<IService>(url, {
		method: "PUT",
		body: JSON.stringify(createData),
	});
}

export function apiRemoveService(serviceId: number, lang: string) {
	const url = `${getApiPath("admin")}/service/${serviceId}${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "DELETE",
	});
}

export function apiGetProjectApiKeyAvailableServices<T extends keyof IApiCall>(type: T, projectId: number, lang: string) {
	const url = `${getApiPath(type)}/project/${projectId}/apikey-available-services${getSearchParams({ lang })}`;

	return fetchJSON<Array<IService>>(url);
}

export function apiGetProjectAllServices(projectId: number, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/service${getSearchParams({ lang })}`;

	return fetchJSON<Array<IService>>(url);
}

export function apiUpdateProjectLinkService(projectId: number, serviceId: number, lastUpdate: string, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/service/${serviceId}${getSearchParams({ lang })}`;

	return fetchJSON<Array<IService>>(url, {
		method: "PUT",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

export function apiUpdateProjectUnlinkService(projectId: number, serviceId: number, lastUpdate: string, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/service/${serviceId}${getSearchParams({ lang })}`;

	return fetchJSON<Array<IService>>(url, {
		method: "DELETE",
		body: JSON.stringify({
			lastUpdate,
		}),
	});
}

export function apiChangeProjectDebtConsumption(projectId: number, data: IChangeDebtConsumptionData, lang: string) {
	const url = `${getApiPath("admin")}/project/${projectId}/wallet-debt-consumption${getSearchParams({ lang })}`;

	return fetchJSON<ILastUpdateOutput>(url, {
		method: "PUT",
		body: JSON.stringify(data),
	});
}

/* ********** admin - transactions ************** */
export function apiGetTransactions(lang: string) {
	const url = `${getApiPath("admin")}/transaction-approval/${getSearchParams({ lang })}`;

	return fetchJSON<Array<ITransactionApproval>>(url);
}

export function apiTransactionApprove(transactionId: string, lang: string) {
	const url = `${getApiPath("admin")}/transaction-approval/${transactionId}${getSearchParams({ lang })}`;

	return fetchJSON<IService>(url, {
		method: "POST",
	});
}

/* ********** admin - reports ************** */
export function apiGetReports(businessPlanType: string, billingMethod: string, lang: string) {
	const url = `${getApiPath("admin")}/reports/${getSearchParams({
		lang,
		...businessPlanType
			? {
				businessPlanType,
			}
			: {},
		...billingMethod
			? {
				billingMethod,
			}
			: {},
	})}`;

	return fetchJSON<Array<IReportItem>>(url, {
		signal: abortFetchSignal(EMAIL_REQUEST_TIMEOUT),
	});
}

/* ********** admin - billing export ************** */
export function apiGetBillingExport(exportType: TExportType, exportDate: string, lang: string) {
	const url = `${getApiPath("admin")}/billing-export/${getSearchParams({
		lang,
		exportDate,
		exportType,
	})}`;

	return fetchFile(url, {
		signal: abortFetchSignal(EMAIL_REQUEST_TIMEOUT),
	});
}

/* ********** admin - catalog ************** */
export function apiGetCatalogs(filter: IAdminCatalogsFilter, lang: string) {
	const searchParams = getFilterParams(filter, lang);
	const url = `${getApiPath("admin")}/catalog/${getSearchParams(searchParams)}`;

	return fetchJSON<Array<ICatalogItem>, IAdminCatalogsPagination>(url);
}

/* ********** admin - rychly komentar ************** */
export async function apiSearchQuickComment(quickCommentData: IAdminQuickCommentData, lang: string) {
	const usp = new URLSearchParams();

	quickCommentData.apiKeys.length > 0 && usp.append("apiKeys", quickCommentData.apiKeys.join(","));
	quickCommentData.emails.length > 0 && usp.append("emails", quickCommentData.emails.join(","));
	quickCommentData.projectOrApiKeyNames.length > 0 && usp.append("projectOrApiKeyNames", quickCommentData.projectOrApiKeyNames.join(","));
	usp.append("lang", lang);

	const url = `${getApiPath("admin")}/project-metadata/apikeys-emails?${usp.toString()}`;
	const data = await fetchJSON<IAdminProjectMetaData>(url);
	let errors: Array<string> = [];
	const items: Array<IQuickCommentItem> = [];
	let ind = 0;

	if (data.noErrors) {
		for (const apiKey of data.data.apiKeys) {
			items.push({
				id: ind,
				projectId: apiKey.projectId,
				projectTitle: apiKey.projectInternalName,
				type: "api-key-id",
				value: apiKey.apiKey,
			});
			ind++;
		}

		for (const apiKey of data.data.projectOrApiKeyNames) {
			items.push({
				id: ind,
				projectId: apiKey.projectId,
				projectTitle: apiKey.projectInternalName,
				type: "api-key-name",
				value: apiKey.projectOrApiKeyName,
			});
			ind++;
		}

		for (const apiKey of data.data.emails) {
			items.push({
				id: ind,
				projectId: apiKey.projectId,
				projectTitle: apiKey.projectInternalName,
				type: "email",
				value: apiKey.email,
			});
			ind++;
		}
	} else {
		errors = data.errors;
	}

	return {
		items,
		errors,
	};
}

/* ********** admin - uzivatele ************** */
export function apiGetAdminUsers(lang: string) {
	const url = `${getApiPath("admin")}/admin-users/${getSearchParams({
		lang,
	})}`;

	return fetchJSON<Array<IUserAdmin>>(url);
}

export function apiCreateAdminUser(lang: string, updateData: IUpdateAdminUserData) {
	const url = `${getApiPath("admin")}/admin-users/${getSearchParams({
		lang,
	})}`;

	return fetchJSON<IUserAdmin>(url, {
		method: "POST",
		body: JSON.stringify(updateData),
	});
}

export function apiUpdateAdminUser(lang: string, updateData: IUpdateAdminUserData) {
	const url = `${getApiPath("admin")}/admin-users/${getSearchParams({
		lang,
	})}`;

	return fetchJSON<IUserAdmin>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiResetAdminUser(lang: string, userId: number) {
	const url = `${getApiPath("admin")}/admin-users/${userId}${getSearchParams({
		lang,
	})}`;

	return fetchJSON<IUserAdmin>(url, {
		method: "DELETE",
	});
}

/* ********** admin - interni uzivatele ************** */
export function apiGetInternalUsers(lang: string) {
	const url = `${getApiPath("admin")}/user/${getSearchParams({ lang })}`;

	return fetchJSON<Array<IInternalUser>>(url);
}

export function apiCreateInternalUser(createData: ICreateInternalUser, lang: string) {
	const url = `${getApiPath("admin")}/user/${getSearchParams({ lang })}`;

	return fetchJSON<IInternalUser>(url, {
		method: "POST",
		body: JSON.stringify(createData),
	});
}

export function apiUpdateInternalUser(userId: number, updateData: IUpdateInternalUser, lang: string) {
	const url = `${getApiPath("admin")}/user/${userId}${getSearchParams({ lang })}`;

	return fetchJSON<IInternalUser>(url, {
		method: "PUT",
		body: JSON.stringify(updateData),
	});
}

export function apiRemoveInternalUser(userId: number, lang: string) {
	const url = `${getApiPath("admin")}/user/${userId}${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "DELETE",
	});
}

export async function apiCreateProjectWithKey<T extends keyof IApiCall>(type: T, createData: ICreateProject[T], lang: string): Promise<ICreateProjectWithKey[T]> {
	const projectData = await apiCreateProject(type, createData, lang);
	const output: ICreateProjectWithKey[T] = {
		data: null,
		dataKey: null,
		status: 200,
		errors: [],
		noErrors: true,
	};

	// bez chyb
	if (projectData.noErrors) {
		const project = projectData.data;

		if (type === "client") {
			const createClientKey: IUpdateKey["client"] = {
				lastUpdate: project.lastUpdate,
				restrictions: {},
				userDescription: "",
				userSwitch: true,
				userTitle: NEW_KEY_NAME,
			};
			const clientKeyData = await apiCreateKey("client", project.id, createClientKey, lang);

			if (clientKeyData.noErrors) {
				output.dataKey = clientKeyData.data;
				project.lastUpdate = clientKeyData.lastUpdate;
			} else {
				output.status = clientKeyData.status;
				output.errors = clientKeyData.errors;
			}
		} else {
			const createAdminKey: IUpdateKey["admin"] = {
				lastUpdate: project.lastUpdate,
				internalDescription: "",
				restrictions: {},
				userDescription: "",
				userSwitch: true,
				userTitle: NEW_KEY_NAME,
			};
			const adminKeyData = await apiCreateKey("admin", project.id, createAdminKey, lang);

			if (adminKeyData.noErrors) {
				output.dataKey = adminKeyData.data;
				project.lastUpdate = adminKeyData.lastUpdate;
			} else {
				output.status = adminKeyData.status;
				output.errors = adminKeyData.errors;
			}
		}

		output.data = project;
	} else {
		output.status = projectData.status;
		output.errors = projectData.errors;
	}

	output.noErrors = output.errors.length === 0;

	return output;
}

export function apiSendBulkEmail(emailData: IAdminBulkEmailData, lang: string) {
	const url = `${getApiPath("admin")}/mailing/${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "POST",
		body: JSON.stringify(emailData),
		signal: abortFetchSignal(EMAIL_REQUEST_TIMEOUT),
	});
}

export function apiSendTestEmail(emailData: IAdminTestEmailData, lang: string) {
	const url = `${getApiPath("admin")}/mailing/test${getSearchParams({ lang })}`;

	return fetchJSON<null>(url, {
		method: "POST",
		body: JSON.stringify(emailData),
		signal: abortFetchSignal(EMAIL_REQUEST_TIMEOUT),
	});
}
